import type { Machine } from "@/schemas/machine";
import type { MachineGroup } from "@/schemas/machine-group";
import type { Corrugate } from "@/schemas";

type ConfigurationAuditData = Record<string, unknown> & {
	Data?: Record<string, unknown>;
	UserName?: string;
	AssignedOperator?: string;
	IotEdgeModuleId?: string;
	Status?: string;
	ProductionStatus?: string;
	Error?: string;
	IsReadyForWork?: boolean;
	LastUpdated?: string;
	AssignedOperatorId?: string;
	ProductionEnabled?: boolean;
};

type MachineGroupAuditData = Record<string, unknown> & {
	UserName?: string;
	AssignedOperator?: string;
	AssignedOperatorId?: string;
	Status?: string;
	ProductionStatus?: string;
	ProductionEnabled?: boolean;
	ConfiguredMachinesWithMachineType?: Record<string, string>;
	ConfiguredMachines?: string[];
};

type ProductionGroupAuditData = Record<string, unknown> & {
	UserName?: string;
	ConfiguredCorrugates?: string[];
	ConfiguredMachineGroups?: string[];
};

const CONFIGURATION_TOP_LEVEL_EXCLUDED_FIELDS = [
	"UserName",
	"AssignedOperator",
	"IotEdgeModuleId",
	"Status",
	"ProductionStatus",
	"Error",
	"IsReadyForWork",
	"LastUpdated",
	"AssignedOperatorId",
	"ProductionEnabled",
] as const;

const CONFIGURATION_NESTED_EXCLUDED_FIELDS = ["Id", "TenantId", "Hash", "additionalTenants"] as const;

const CONFIGURATION_REDACTED_FIELDS = ["UserName", "Email", "PhoneNumber", "FirstName", "LastName"] as const;

const MACHINE_EXCLUDED_FIELDS = [
	"UserName",
	"AssignedOperator",
	"IotEdgeModuleId",
	"Status",
	"ProductionStatus",
	"Error",
	"IsReadyForWork",
	"LastUpdated",
] as const;

const MACHINE_GROUP_EXCLUDED_FIELDS = [
	"UserName",
	"AssignedOperator",
	"AssignedOperatorId",
	"Status",
	"ProductionStatus",
	"ProductionEnabled",
] as const;

const BASIC_EXCLUDED_FIELDS = ["UserName"] as const;

export const sanitizeBasicAuditData = (data: unknown): Record<string, unknown> => {
	if (data == null || typeof data !== "object") {
		return {};
	}

	const auditData = data as Record<string, unknown>;

	for (const field of BASIC_EXCLUDED_FIELDS) {
		delete auditData[field];
	}

	return auditData;
};

export const sanitizeMachineAuditData = (data: unknown): Record<string, unknown> => {
	if (data == null || typeof data !== "object") {
		return {};
	}

	const auditData = data as Record<string, unknown>;

	for (const field of MACHINE_EXCLUDED_FIELDS) {
		delete auditData[field];
	}

	return auditData;
};

export const sanitizeConfigurationAuditData = (data: unknown, redactedLabel: string): Record<string, unknown> => {
	if (data == null || typeof data !== "object") {
		console.error("Data for message was invalid or null");
		return {};
	}

	const auditData = data as ConfigurationAuditData;

	if (auditData.Data && typeof auditData.Data === "object") {
		for (const field of CONFIGURATION_NESTED_EXCLUDED_FIELDS) {
			delete auditData.Data[field];
		}

		for (const field of CONFIGURATION_REDACTED_FIELDS) {
			auditData.Data[field] = redactedLabel;
		}
	}

	for (const field of CONFIGURATION_TOP_LEVEL_EXCLUDED_FIELDS) {
		delete auditData[field];
	}

	return auditData;
};

export const sanitizeMachineGroupAuditData = (data: unknown, machineList: Machine[]): Record<string, unknown> => {
	if (!data) return data as Record<string, unknown>;

	const auditData = data as MachineGroupAuditData;

	for (const field of MACHINE_GROUP_EXCLUDED_FIELDS) {
		delete auditData[field];
	}

	if (auditData.ConfiguredMachinesWithMachineType && machineList?.length) {
		const machineNames = Object.keys(auditData.ConfiguredMachinesWithMachineType).map((machineId) => {
			const machine = machineList.find((m) => m.id === machineId);
			return machine?.alias || machineId;
		});
		delete auditData.ConfiguredMachinesWithMachineType;
		auditData.ConfiguredMachines = machineNames;
	}

	return auditData;
};

export const sanitizeProductionGroupAuditData = (
	data: unknown,
	corrugates: Corrugate[],
	machineGroups: MachineGroup[],
): Record<string, unknown> => {
	const auditData = data as ProductionGroupAuditData;

	for (const field of BASIC_EXCLUDED_FIELDS) {
		delete auditData[field];
	}

	const corrugateNames = (auditData.ConfiguredCorrugates ?? []).map((corrugateId: string) => {
		const corrugate = corrugates.find((c) => c.id === corrugateId);
		return corrugate?.alias ?? corrugateId;
	});
	auditData.ConfiguredCorrugates = corrugateNames;

	const machineGroupNames = (auditData.ConfiguredMachineGroups ?? []).map((machineGroupId: string) => {
		const machineGroup = machineGroups.find((mg) => mg.id === machineGroupId);
		return machineGroup?.alias ?? machineGroupId;
	});
	auditData.ConfiguredMachineGroups = machineGroupNames;

	return auditData;
};
